import React, { useId } from "react";
import { Check } from "lucide-react";
import { focusableDisabledProps } from "../../lib/focusable-disabled.js";
import { STATE_RING } from "../../lib/state-ring.js";
import { TONES, type StatusTone } from "../../lib/tones.js";
import { cn } from "../../lib/utils.js";
import { usePopoverContext } from "./popover-context.js";

/** Action severity: the subset of `StatusTone` a menu action can mean. */
export type PopoverMenuItemTone = Extract<StatusTone, "neutral" | "critical">;

/* Every state paints from its prop, never from an aria-* selector, so a raw
   aria-current or aria-disabled passed through `rest` keeps today's look. */
/** ActionMenu's red hover wash for a critical action. */
const CRITICAL_WASH = "hover:bg-pb-red/10";
/** The teal selection fill (C7: teal marks the selected state only), ringed
 *  in high contrast where the tint alone is too faint (d15). */
const CURRENT = `bg-pb-teal-soft ${STATE_RING.teal}`;
/** Select's active-option paint: the roving highlight, not a selection, so
 *  not teal. High contrast paints the overlay at 0%, so the neutral state ring
 *  shows where the arrow keys are (d16). */
const ACTIVE = `pb-overlay-paint ${STATE_RING.neutral}`;
/* The base string's disabled: dim as plain classes, because aria-disabled
   does not match :disabled. The bg-none pair drops the hover and press paint. */
const FOCUSABLE_DISABLED_DIM =
  "opacity-40 cursor-not-allowed hover:bg-none active:bg-none";

export interface PopoverMenuItemProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  icon?: React.ElementType;
  trailing?: React.ReactNode;
  checked?: boolean;
  closeOnSelect?: boolean;
  /**
   * Secondary line rendered INSIDE the button, under `children`.
   *
   * Exists because the single-line body truncates, so a consumer needing two lines
   * previously had to hoist the text out as a sibling inside `role="none"` (see
   * `ActionMenu`) — which puts it outside the focus ring and the hit target, so the row
   * stops reading as one object. Announced via `aria-describedby`, never by joining the
   * accessible name.
   */
  description?: React.ReactNode;
  /**
   * Replaces the fixed `w-4 h-4` icon slot for callers needing a larger leading visual.
   * `icon` is unaffected and still renders when `leading` is absent.
   */
  leading?: React.ReactNode;
  /** Action severity. `critical` paints the label and icon in the critical
   *  tone text (lib/tones.ts) with a red hover wash. Default `neutral`. */
  tone?: PopoverMenuItemTone | undefined;
  /** The roving keyboard highlight for an aria-activedescendant list (Select's
   *  active-option paint). A chosen item uses `checked` or `current`. */
  active?: boolean | undefined;
  /** Sets aria-current (`true` becomes "true") and paints the teal selection
   *  fill, as ListItem does. A raw aria-current alone paints nothing. */
  current?: boolean | "page" | "step" | "location" | undefined;
  /**
   * Button's `focusableWhenDisabled` contract (atoms/Button/Button.tsx,
   * button-pressed-busy), applied to a menu item. While `disabled`:
   * - the item stays in the menu's roving ring;
   * - selecting it does not run `closeOnSelect`.
   */
  focusableWhenDisabled?: boolean | undefined;
}

export function PopoverMenuItem({
  icon: Icon,
  trailing,
  checked,
  closeOnSelect = true,
  className,
  children,
  onClick,
  disabled,
  description,
  leading,
  tone = "neutral",
  active = false,
  current = false,
  focusableWhenDisabled = false,
  ...rest
}: PopoverMenuItemProps) {
  const { setOpen, restoreFocusOnCloseRef } = usePopoverContext();
  const scope = useId();
  const labelId = `${scope}-label`;
  const descId = `${scope}-desc`;
  const critical = tone === "critical";
  const dimmed = Boolean(disabled) && focusableWhenDisabled;
  /* The shared contract's attributes; its click guard, when present,
     replaces `select` so onClick and closeOnSelect never run. */
  const { onClick: blockClick, ...disabledAttrs } =
    focusableDisabledProps<HTMLButtonElement>({
      inactive: Boolean(disabled),
      focusableWhenDisabled,
    });

  /* Merge, never replace: `{...rest}` is spread before these, so a consumer-supplied
     aria-describedby would otherwise clobber the description's own id and silently
     unannounce it. Collapses to `undefined` rather than "" — an empty attribute is
     PRESENT, which breaks callers asserting `.not.toHaveAttribute`. */
  const describedBy =
    [description !== undefined ? descId : null, rest["aria-describedby"]]
      .filter(Boolean)
      .join(" ") || undefined;

  const select = (e: React.MouseEvent<HTMLButtonElement>): void => {
    onClick?.(e);
    if (closeOnSelect && !e.defaultPrevented) {
      setOpen(false);
      /* Without this the select path drops focus to <body>; only the Escape path
         restored it.

         Deferred and guarded on `isConnected` because a menu item may destroy its
         own trigger — "Remove" on the row that owns the menu is the common shape.
         The consumer then owns where focus lands (a control that survives), and
         focusing a detached trigger here would send focus to <body> and silently
         beat them to it. The consumer's handler has run by this point but React
         has not committed its removal yet, so the check has to happen after the
         commit rather than inline. */
      restoreFocusOnCloseRef.current = true;
    }
  };

  return (
    <button
      type="button"
      role="menuitem"
      {...disabledAttrs}
      onClick={blockClick ?? select}
      className={cn(
        "w-full flex items-center gap-2 px-2.5 py-1.5 rounded-sm text-sm text-pb-text hover:pb-overlay-paint active:pb-overlay-paint-strong text-start transition-colors focus-ring disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-none",
        critical && TONES.critical.text,
        critical && !dimmed && CRITICAL_WASH,
        current && CURRENT,
        active && ACTIVE,
        dimmed && FOCUSABLE_DISABLED_DIM,
        className,
      )}
      {...rest}
      {...(current
        ? { "aria-current": current === true ? "true" : current }
        : {})}
      {...(description !== undefined ? { "aria-labelledby": labelId } : {})}
      {...(describedBy !== undefined
        ? { "aria-describedby": describedBy }
        : {})}
    >
      {leading !== undefined ? (
        leading
      ) : Icon ? (
        <Icon
          className={cn(
            "w-4 h-4 text-pb-text-secondary shrink-0",
            critical && TONES.critical.text,
          )}
        />
      ) : (
        <span aria-hidden className="w-4 h-4 shrink-0" />
      )}
      {description !== undefined ? (
        <span className="flex-1 min-w-0">
          <span id={labelId} className="block truncate">
            {children}
          </span>
          <span
            id={descId}
            className="block truncate text-dense-2 text-pb-text-muted"
          >
            {description}
          </span>
        </span>
      ) : (
        <span className="flex-1 truncate">{children}</span>
      )}
      {checked !== undefined ? (
        <span className="shrink-0 w-4 h-4 flex items-center justify-center text-pb-teal-text">
          {checked && <Check className="w-3.5 h-3.5" />}
        </span>
      ) : (
        trailing
      )}
    </button>
  );
}
